import { useEffect } from "react";

interface DetailPanelProps {
  open: boolean;
  onClose: () => void;
  eyebrow?: string;
  title: string;
  accent?: "cyber" | "ok" | "warn" | "crit";
  children: React.ReactNode;
}

const ACCENT: Record<NonNullable<DetailPanelProps["accent"]>, string> = {
  cyber: "border-cyber-400/40 shadow-glow-cyber",
  ok: "border-ok/40 shadow-glow-ok",
  warn: "border-warn/40 shadow-glow-warn",
  crit: "border-crit/40 shadow-glow-crit",
};

const NOTCH_CLIP =
  "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%)";

export function DetailPanel({
  open,
  onClose,
  eyebrow,
  title,
  accent = "cyber",
  children,
}: DetailPanelProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close detail panel"
        onClick={onClose}
        className="fixed inset-0 z-30 cursor-default bg-ink-900/60 backdrop-blur-sm"
      />
      <aside
        role="dialog"
        aria-label={title}
        className={[
          "fixed right-0 top-0 z-40 flex h-screen w-[420px] flex-col border-l bg-ink-700",
          "animate-flicker_in",
          ACCENT[accent],
        ].join(" ")}
        style={{ clipPath: NOTCH_CLIP }}
      >
        <div className="flex items-start justify-between border-b border-ink-400 px-6 pt-6 pb-4">
          <div>
            {eyebrow && <div className="label-overline">{eyebrow}</div>}
            <h2 className="mt-1 text-lg font-semibold text-chrome-50">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-chrome-400 transition hover:bg-ink-600 hover:text-chrome-100"
          >
            <span aria-hidden>✕</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyber-400/40 to-transparent" />
      </aside>
    </>
  );
}

interface KeyValueProps {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}

export function KeyValue({ label, children, mono = false }: KeyValueProps): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-ink-400 py-2.5 last:border-0">
      <span className="text-xs text-chrome-400">{label}</span>
      <span
        className={[
          "max-w-[60%] truncate text-right text-sm text-chrome-100",
          mono && "code",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </span>
    </div>
  );
}

export function PanelSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="mt-6 first:mt-0">
      <div className="label-overline mb-2">{title}</div>
      {children}
    </section>
  );
}
